/**
 * Direct AppSync GraphQL for ProgramReferralCode / ProgramReferralSignup.
 * Works when amplify_outputs model_introspection is stale (localhost after cloud schema deploy).
 */
import { runGraphql } from './pilotInviteGraphql';

export type ProgramReferralCodeRecord = {
  id: string;
  ownerUserId: string;
  ownerRole?: string | null;
  code: string;
  organizationId?: string | null;
  active?: boolean | null;
  createdAt: string;
};

export type ProgramReferralSignupRecord = {
  id: string;
  codeId: string;
  code: string;
  referrerUserId: string;
  referredUserId: string;
  referredRole?: string | null;
  referredEmail?: string | null;
  referredName?: string | null;
  productSurface?: string | null;
  registeredAt: string;
};

const CODE_FIELDS = `
  id
  ownerUserId
  ownerRole
  code
  organizationId
  active
  createdAt
`;

const SIGNUP_FIELDS = `
  id
  codeId
  code
  referrerUserId
  referredUserId
  referredRole
  referredEmail
  referredName
  productSurface
  registeredAt
`;

const LIST_CODES = /* GraphQL */ `
  query ListProgramReferralCodes($filter: ModelProgramReferralCodeFilterInput, $limit: Int, $nextToken: String) {
    listProgramReferralCodes(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { ${CODE_FIELDS} }
      nextToken
    }
  }
`;

const CREATE_CODE = /* GraphQL */ `
  mutation CreateProgramReferralCode($input: CreateProgramReferralCodeInput!) {
    createProgramReferralCode(input: $input) { ${CODE_FIELDS} }
  }
`;

const LIST_SIGNUPS = /* GraphQL */ `
  query ListProgramReferralSignups($filter: ModelProgramReferralSignupFilterInput, $limit: Int, $nextToken: String) {
    listProgramReferralSignups(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { ${SIGNUP_FIELDS} }
      nextToken
    }
  }
`;

const CREATE_SIGNUP = /* GraphQL */ `
  mutation CreateProgramReferralSignup($input: CreateProgramReferralSignupInput!) {
    createProgramReferralSignup(input: $input) { ${SIGNUP_FIELDS} }
  }
`;

function isSchemaNotDeployedError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("cannot query field 'listprogramreferralcodes'") ||
    m.includes("cannot query field 'listprogramreferralsignups'") ||
    m.includes('unknown type modelprogramreferralcode') ||
    m.includes('unknown type modelprogramreferralsignup') ||
    m.includes('unknown type createprogramreferralcodeinput') ||
    m.includes('unknown type createprogramreferralsignupinput')
  );
}

export function formatProgramReferralApiError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  if (isSchemaNotDeployedError(message)) {
    return 'Program referral is not available yet. Deploy the data schema, then try again.';
  }
  return message;
}

export async function listProgramReferralCodesGraphql(filter?: Record<string, unknown>, limit = 200): Promise<ProgramReferralCodeRecord[]> {
  const rows: ProgramReferralCodeRecord[] = [];
  let nextToken: string | null | undefined = undefined;
  do {
    const data = await runGraphql<{
      listProgramReferralCodes: {
        items: Array<ProgramReferralCodeRecord | null> | null;
        nextToken?: string | null;
      };
    }>(LIST_CODES, { filter, limit, nextToken }, 'apiKey');
    const items = (data.listProgramReferralCodes.items || []).filter(Boolean) as ProgramReferralCodeRecord[];
    rows.push(...items);
    nextToken = data.listProgramReferralCodes.nextToken;
  } while (nextToken && rows.length < limit);
  return rows.slice(0, limit);
}

export async function createProgramReferralCodeGraphql(input: {
  ownerUserId: string;
  ownerRole: string;
  code: string;
  organizationId?: string;
  active: boolean;
  createdAt: string;
}): Promise<ProgramReferralCodeRecord> {
  const data = await runGraphql<{
    createProgramReferralCode: ProgramReferralCodeRecord | null;
  }>(CREATE_CODE, { input }, 'apiKey');
  if (!data.createProgramReferralCode) {
    throw new Error('Failed to create referral code');
  }
  return data.createProgramReferralCode;
}

export async function listProgramReferralSignupsGraphql(
  filter?: Record<string, unknown>,
  limit = 500,
): Promise<ProgramReferralSignupRecord[]> {
  const rows: ProgramReferralSignupRecord[] = [];
  let nextToken: string | null | undefined = undefined;
  do {
    const data = await runGraphql<{
      listProgramReferralSignups: {
        items: Array<ProgramReferralSignupRecord | null> | null;
        nextToken?: string | null;
      };
    }>(LIST_SIGNUPS, { filter, limit, nextToken }, 'apiKey');
    const items = (data.listProgramReferralSignups.items || []).filter(Boolean) as ProgramReferralSignupRecord[];
    rows.push(...items);
    nextToken = data.listProgramReferralSignups.nextToken;
  } while (nextToken && rows.length < limit);
  return rows.slice(0, limit);
}

export async function createProgramReferralSignupGraphql(input: {
  codeId: string;
  code: string;
  referrerUserId: string;
  referredUserId: string;
  referredRole?: string;
  referredEmail?: string;
  referredName?: string;
  productSurface?: string;
  registeredAt: string;
}): Promise<ProgramReferralSignupRecord> {
  const data = await runGraphql<{
    createProgramReferralSignup: ProgramReferralSignupRecord | null;
  }>(CREATE_SIGNUP, { input }, 'apiKey');
  if (!data.createProgramReferralSignup) {
    throw new Error('Failed to create referral signup');
  }
  return data.createProgramReferralSignup;
}
